import React, { useEffect, useState } from "react";
import { PageHero, Section, SectionTitle, Reveal, CTALink, Eyebrow } from "@/components/Editorial";
import { fetchRegions } from "@/lib/api";
import { IndiaDotMap, INDIA_MARKERS } from "@/home/IndiaDotMap";

const CHANNEL_SECTIONS = [
  { title: "Hospitality", body: "Luxury hotels, fine dining and premium bars are served with structured supply, portfolio guidance and dependable availability." },
  { title: "Retail", body: "Premium retail and bottle shops receive curated assortments supported by consistent replenishment." },
  { title: "Diplomatic & Institutional", body: "Diplomatic missions and institutional clients are supplied through experienced, compliant handling and discreet service." },
  { title: "Airports & Airlines", body: "Travel retail and airline supply handled with the documentation discipline the channel requires." },
  { title: "Modern & General Trade", body: "Modern trade and general trade coverage built on long-standing relationships and structured distribution." },
  { title: "Delhi NCR Direct Logistics", body: "A company-owned fleet and permanent logistics team provide direct last-mile supply across Delhi NCR." },
  { title: "Partner Model Beyond NCR", body: "In other cities and states, HCC works through established distribution partners with local channel knowledge." },
];

export default function DistributionPage() {
  const [regions, setRegions] = useState([]);
  const [active, setActive] = useState("Delhi NCR");

  useEffect(() => {
    fetchRegions().then(setRegions);
  }, []);

  const activeRegion = regions.find((r) => r.state_name === active);

  return (
    <div data-testid="distribution-page" className="bg-[#F7F1E7]">
      <PageHero
        dark
        eyebrow="Distribution"
        title="Market access built through relationships."
        lede="Every region, trade channel and regulatory environment requires a different approach. HCC combines direct logistics in Delhi NCR with established distribution partners across India."
      />

      <Section dark className="pt-0">
        <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl">
          <Reveal className="max-w-md">
            <IndiaDotMap interactive activeName={active} onSelect={setActive} />
            <p className="mt-3 text-[10px] tracking-[0.2em] uppercase text-[#F7F1E7]/35">
              Abstract representation of market presence — not a survey map.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="region-chips">
              {regions.map((r) => (
                <button
                  key={r.state_name}
                  onClick={() => setActive(r.state_name)}
                  data-testid={`region-chip-${r.state_name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3.5 py-2 text-[10px] uppercase tracking-[0.12em] border transition-colors duration-300 ${
                    active === r.state_name
                      ? "bg-[#C7A45D] text-[#111111] border-[#C7A45D]"
                      : "border-white/20 text-[#F7F1E7]/70 hover:border-[#C7A45D]/60"
                  }`}
                >
                  {r.state_name}
                </button>
              ))}
            </div>
            {activeRegion && (
              <div data-testid="region-detail" className="border border-white/15 px-8 py-8">
                <h3 className="hcc-serif text-2xl text-[#F7F1E7]">{activeRegion.state_name}</h3>
                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#C7A45D]">{activeRegion.coverage_type}</p>
                <p className="mt-4 text-sm font-light leading-relaxed text-[#F7F1E7]/65">{activeRegion.description}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {activeRegion.channels.map((c) => (
                    <span key={c} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] border border-white/15 text-[#F7F1E7]/70">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </Section>

      <Section>
        <SectionTitle eyebrow="Channels" title="Coverage across the channels that shape preference." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10 max-w-6xl">
          {CHANNEL_SECTIONS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06}>
              <Eyebrow>{String(i + 1).padStart(2, "0")}</Eyebrow>
              <h3 className="hcc-serif text-xl mt-3 text-[#1c1713]">{c.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-[#1c1713]/65">{c.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tint className="text-center">
        <Reveal>
          <h2 className="hcc-serif text-3xl font-medium max-w-xl mx-auto">Discuss distribution for your market.</h2>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <CTALink to="/contact" solid testId="distribution-contact-cta">Send a Trade Enquiry</CTALink>
            <CTALink to="/partner" testId="distribution-partner-cta">Partner With HCC</CTALink>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
